import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Info, Trash2, Search } from 'lucide-react';
import api from '../utils/api';
import { formatDateBR, todayLocalISO } from '../utils/formatDate';
import toast from 'react-hot-toast';
import AnimalSearchSelect from '../components/AnimalSearchSelect';

const FAMACHA_INFO = [
  { escore: 1, cor: '#DC2626', bg: '#FEE2E2', corOlho: '#DC2626', desc: 'Vermelho vivo — Sem anemia. Não vermifugar.' },
  { escore: 2, cor: '#E87070', bg: '#FEE2E2', corOlho: '#E87070', desc: 'Vermelho-rosado — Sem anemia. Não vermifugar.' },
  { escore: 3, cor: '#F5A0A0', bg: '#FFF1F1', corOlho: '#F5A0A0', desc: 'Rosado — Suspeito. Avaliar com outros sinais.' },
  { escore: 4, cor: '#F5CEC0', bg: '#FFF8F5', corOlho: '#F5CEC0', desc: 'Rosado claro — Anemia moderada. Vermifugar.' },
  { escore: 5, cor: '#E5E5E5', bg: '#FAFAFA', corOlho: '#E5E5E5', desc: 'Branco — Anemia grave. Vermifugar urgente.' },
];

const CATEGORIAS = ['cordeiro','borrego','reprodutor','matriz'];

const ALERTA_LABEL = { 3:'Atenção', 4:'Tratamento recomendado', 5:'Tratamento urgente' };
const ALERTA_COR   = { 3:'#92400E', 4:'#7C2D12', 5:'#991B1B' };
const ALERTA_BG    = { 3:'#FFF1F1', 4:'#FFF8F5', 5:'#FEE2E2' };

export default function Famacha() {
  const [data, setData] = useState([]);
  const [animais, setAnimais] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showGuia, setShowGuia] = useState(true); // Guia aberta por padrão ao abrir a tela
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState({ animal_id:'', escore:'', lote_id:'', categoria:'', data_inicio:'', data_fim:'', busca:'' });
  const [form, setForm] = useState({ animal_id:'', data:todayLocalISO(), escore:3, vermifugado:false, produto_id:'', dose:'', observacoes:'', data_proxima_famacha:'' });
  const [sort, setSort] = useState({ col:'data', dir:'desc' });

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data:d } = await api.get('/sanitario/famacha', { params:filtro }); setData(d); }
    finally { setLoading(false); }
  }, [filtro]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/animais', { params:{ situacao:'ativo', limit:500 } }).then(r => setAnimais(r.data.data || r.data));
    api.get('/lotes').then(r => setLotes(r.data));
    api.get('/estoque/produtos', { params:{ tipo:'vermifugo' } }).then(r => setProdutos(r.data));
  }, []);

  const salvar = async () => {
    try {
      await api.post('/sanitario/famacha', form);
      toast.success('Famacha registrado!');
      setShowModal(false);
      setForm(p => ({ ...p, animal_id:'', escore:3, vermifugado:false, dose:'', data_proxima_famacha:'' }));
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir este registro Famacha?')) return;
    try { await api.delete(`/sanitario/famacha/${id}`); toast.success('Excluído'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  const getInfo = (e) => FAMACHA_INFO.find(f => f.escore === e) || FAMACHA_INFO[2];
  const dist = [1,2,3,4,5].map(e => ({ escore:e, total:data.filter(d => d.escore === e).length }));

  const toggleSort = (col) => setSort(s => ({ col, dir: s.col === col && s.dir === 'asc' ? 'desc' : 'asc' }));
  const sortedData = [...data].sort((a, b) => {
    let va = a[sort.col], vb = b[sort.col];
    if (!va && !vb) return 0; if (!va) return 1; if (!vb) return -1;
    if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb||'').toLowerCase(); }
    const r = va < vb ? -1 : va > vb ? 1 : 0;
    return sort.dir === 'asc' ? r : -r;
  });
  const Th = ({ col, children }) => (
    <th onClick={() => toggleSort(col)} style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }}>
      {children}{sort.col===col ? (sort.dir==='asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div><h1>Famacha</h1><p>Controle de verminose por escore conjuntival · {data.length} registros</p></div>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary" onClick={() => setShowGuia(!showGuia)}><Info size={16} /> Guia</button>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}><Plus size={16} /> Registrar</button>
        </div>
      </div>

      {showGuia && (
        <div className="card" style={{ marginBottom:16, background:'var(--bg-main)' }}>
          <h3 style={{ marginBottom:12 }}>Guia de Escores Famacha</h3>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {FAMACHA_INFO.map(f => (
              <div key={f.escore} style={{ flex:1, minWidth:160, padding:12, borderRadius:8, border:`2px solid ${f.corOlho}`, background:f.bg }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                  <span style={{ width:28, height:28, borderRadius:'50%', background:f.corOlho, display:'inline-block', border:f.escore === 5 ? '2px solid #ccc' : 'none' }} />
                  <span style={{ fontWeight:700, fontSize:18, color:f.escore <= 2 ? '#991B1B' : f.escore === 3 ? '#92400E' : f.escore === 4 ? '#7C2D12' : '#374151' }}>Escore {f.escore}</span>
                </div>
                <div style={{ fontSize:13, color:'var(--text-primary)' }}>{f.desc}</div>
                {f.escore >= 3 && <div style={{ marginTop:6, fontSize:12, fontWeight:600, color:ALERTA_COR[f.escore] }}>{ALERTA_LABEL[f.escore]}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Indicadores */}
      <div className="stats-grid" style={{ gridTemplateColumns:'repeat(5,1fr)', marginBottom:16 }}>
        {dist.map(d => {
          const info = getInfo(d.escore);
          return (
            <div key={d.escore} className="stat-card" style={{ borderLeft:`4px solid ${info.corOlho}`, textAlign:'center', position:'relative', cursor:'pointer' }}
              onClick={() => setFiltro(p => ({ ...p, escore: p.escore === String(d.escore) ? '' : String(d.escore) }))}>
              <div style={{ position:'absolute', top:8, right:10 }}>
                <span style={{ width:16, height:16, borderRadius:'50%', background:info.corOlho, display:'inline-block', border:d.escore === 5 ? '1px solid #ccc' : 'none' }} />
              </div>
              <div style={{ fontSize:11, color:'var(--text-secondary)', marginBottom:4 }}>ESCORE {d.escore}</div>
              <div style={{ fontSize:24, fontWeight:700, color:d.escore <= 2 ? '#991B1B' : d.escore === 3 ? '#92400E' : d.escore === 4 ? '#7C2D12' : '#374151' }}>{d.total}</div>
            </div>
          );
        })}
      </div>

      {/* Filtros */}
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
          <div className="search-box" style={{ flex:2, minWidth:180 }}>
            <Search size={16} />
            <input placeholder="Buscar por brinco ou nome..." value={filtro.busca} onChange={e => setFiltro(p => ({ ...p, busca:e.target.value }))} />
          </div>
          <div style={{ width:260, minWidth:220 }}>
            <AnimalSearchSelect value={filtro.animal_id} onChange={id => setFiltro(p => ({ ...p, animal_id:id }))} emptyLabel="Todos os animais" />
          </div>
          <select value={filtro.escore} onChange={e => setFiltro(p => ({ ...p, escore:e.target.value }))} style={{ width:'auto' }}>
            <option value="">Todos escores</option>
            {[1,2,3,4,5].map(n => <option key={n} value={n}>Escore {n}</option>)}
          </select>
          <select value={filtro.lote_id} onChange={e => setFiltro(p => ({ ...p, lote_id:e.target.value }))} style={{ width:'auto' }}>
            <option value="">Todos os lotes</option>
            {lotes.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
          </select>
          <select value={filtro.categoria} onChange={e => setFiltro(p => ({ ...p, categoria:e.target.value }))} style={{ width:'auto' }}>
            <option value="">Todas categorias</option>
            {CATEGORIAS.map(c => <option key={c} value={c} style={{ textTransform:'capitalize' }}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
          </select>
          <input type="date" value={filtro.data_inicio} onChange={e => setFiltro(p => ({ ...p, data_inicio:e.target.value }))} title="Data início" style={{ width:145 }} />
          <input type="date" value={filtro.data_fim} onChange={e => setFiltro(p => ({ ...p, data_fim:e.target.value }))} title="Data fim" style={{ width:145 }} />
        </div>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <table>
          <thead><tr><Th col="brinco">Animal</Th><Th col="lote_nome">Lote</Th><Th col="categoria">Categoria</Th><Th col="data">Data</Th><Th col="escore">Escore</Th><th>Alerta</th><Th col="vermifugado">Vermifugado</Th><Th col="produto_nome">Produto</Th><Th col="dose">Dose</Th><th>Obs</th><th></th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={11} style={{ textAlign:'center', padding:40 }}>Carregando...</td></tr>
            : sortedData.length === 0 ? <tr><td colSpan={11}><div className="empty-state"><p>Nenhum registro Famacha</p></div></td></tr>
            : sortedData.map(d => {
              const info = getInfo(d.escore);
              return (
                <tr key={d.id}>
                  <td><strong style={{ color:'var(--color-primary)' }}>{d.brinco}</strong>{d.animal_nome && ` — ${d.animal_nome}`}</td>
                  <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{d.lote_nome || '—'}</td>
                  <td style={{ fontSize:12 }}><span className="badge badge-green" style={{ textTransform:'capitalize' }}>{d.categoria || '—'}</span></td>
                  <td>{formatDateBR(d.data)}</td>
                  <td>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:6, fontWeight:700 }}>
                      <span style={{ width:14, height:14, borderRadius:'50%', background:info.corOlho, display:'inline-block', border:d.escore === 5 ? '1px solid #ccc' : 'none' }} />
                      {d.escore}
                    </span>
                  </td>
                  <td>
                    {d.escore >= 3 ? (
                      <span style={{ padding:'2px 8px', borderRadius:10, fontSize:11, fontWeight:700, color:ALERTA_COR[d.escore], background:ALERTA_BG[d.escore], border:`1px solid ${ALERTA_COR[d.escore]}40` }}>
                        {ALERTA_LABEL[d.escore]}
                      </span>
                    ) : <span style={{ color:'var(--text-secondary)', fontSize:12 }}>Normal</span>}
                  </td>
                  <td>{d.vermifugado ? <span className="badge badge-green">Sim</span> : <span className="badge badge-gray">Não</span>}</td>
                  <td style={{ fontSize:13 }}>{d.produto_nome || '—'}</td>
                  <td style={{ fontSize:13 }}>{d.dose ? `${d.dose} ml` : '—'}</td>
                  <td style={{ fontSize:13, color:'var(--text-secondary)' }}>{d.observacoes || '—'}</td>
                  <td><button className="btn btn-secondary btn-sm" onClick={() => excluir(d.id)} style={{ color:'#DC2626', padding:'4px 8px' }}><Trash2 size={14} /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header"><h2>Registrar Famacha</h2><button className="btn btn-secondary btn-sm" onClick={() => setShowModal(false)}>✕</button></div>
            <div className="modal-body">
              <div className="form-group"><label>Animal *</label>
                <AnimalSearchSelect value={form.animal_id} onChange={id => setForm(p => ({ ...p, animal_id:id }))} required />
              </div>
              <div className="form-row">
                <div className="form-group"><label>Data *</label><input type="date" value={form.data} onChange={e => setForm(p => ({ ...p, data:e.target.value }))} /></div>
                <div className="form-group"><label>Escore Famacha (1–5)</label>
                  <select value={form.escore} onChange={e => setForm(p => ({ ...p, escore:parseInt(e.target.value), vermifugado:parseInt(e.target.value) >= 4 }))}>
                    {[1,2,3,4,5].map(n => <option key={n} value={n}>{n} — {FAMACHA_INFO[n-1].desc.split('—')[0]}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ padding:'10px 12px', borderRadius:8, background:getInfo(form.escore).bg, border:`1px solid ${getInfo(form.escore).corOlho}`, marginBottom:16, fontSize:13, display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ width:22, height:22, borderRadius:'50%', background:getInfo(form.escore).corOlho, flexShrink:0, border:form.escore === 5 ? '1px solid #ccc' : 'none' }} />
                <div>
                  <div>{FAMACHA_INFO[form.escore-1].desc}</div>
                  {form.escore >= 3 && <div style={{ marginTop:3, fontWeight:700, color:ALERTA_COR[form.escore] }}>{ALERTA_LABEL[form.escore]}</div>}
                </div>
              </div>
              <div className="form-group">
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                  <input type="checkbox" checked={form.vermifugado} onChange={e => setForm(p => ({ ...p, vermifugado:e.target.checked }))} style={{ width:'auto' }} />
                  Animal foi vermifugado?
                </label>
              </div>
              {form.vermifugado && (
                <div className="form-row">
                  <div className="form-group"><label>Produto</label>
                    <select value={form.produto_id} onChange={e => setForm(p => ({ ...p, produto_id:e.target.value }))}>
                      <option value="">Selecione</option>
                      {produtos.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label>Dose (ml)</label><input type="number" step="0.1" value={form.dose} onChange={e => setForm(p => ({ ...p, dose:e.target.value }))} /></div>
                </div>
              )}
              <div className="form-group"><label>Observações</label><textarea rows={2} value={form.observacoes} onChange={e => setForm(p => ({ ...p, observacoes:e.target.value }))} /></div>
              {form.escore >= 3 && (
                <div style={{ borderTop:'1px solid var(--border-color)', paddingTop:14, marginTop:4 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--text-secondary)', marginBottom:10, textTransform:'uppercase', letterSpacing:0.5 }}>Reagendamento Famacha</div>
                  <div className="form-group">
                    <label>Data da próxima avaliação Famacha</label>
                    <input type="date" value={form.data_proxima_famacha} onChange={e => setForm(p => ({ ...p, data_proxima_famacha:e.target.value }))} />
                  </div>
                  {form.data_proxima_famacha && <div style={{ fontSize:12, color:'var(--color-warning)', marginTop:-6 }}>⚠ Um lembrete será adicionado à Agenda Consolidada</div>}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvar} disabled={!form.animal_id}>Registrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
